import axios from 'axios';
import { config } from '../config';
import { profileParser } from './profile';
import { contentfulParser, getActiveSite } from './contentful';

// Cache for raw S3 entries data
let entriesCache = null;

/**
 * Call /api/account and return modified response based on user logged in status.
 * @param {object} contentfulData Contentfull parsed response.
 * @param {string} apiHost The host domain for the account request, eg https://spyscape.com.
 */
export const fetchAccount = async (contentfulData, apiHost) => {
  try {
    const account = await axios.get(`${apiHost}/api/account`);
    return profileParser(account.data, contentfulData);
  } catch (e) {
    return profileParser(undefined, contentfulData);
  }
};

/**
 * Fetch per-locale JSON from S3 (cached).
 * Returns raw (unresolved) data — contentfulParser handles link resolution.
 */
const fetchS3Entries = async () => {
  if (entriesCache) return entriesCache;

  const response = await axios.get(config.contentful.s3Url);
  entriesCache = response.data;
  return entriesCache;
};

/**
 * Find entry by ID in S3 data and return a Contentful-shaped response
 * that contentfulParser can consume.
 *
 * S3 exports place all entries in the top-level `items` array with an
 * empty `includes.Entry`, so we copy items into includes.Entry so that
 * contentfulParser's getItemFields can resolve link references.
 */
const findEntryById = (data, entryId) => {
  const allItems = data.items || [];
  const item = allItems.find(i => i.sys?.id === entryId);

  if (!item) {
    return { items: [], includes: {} };
  }

  return {
    items: [item],
    includes: {
      ...data.includes,
      Entry: allItems,
    },
  };
};

/**
 * Fetch data from S3 for specific entry id and return parsed response.
 * Before the parsed response returned, a check perfomed for any subsites on current domain path.
 * If a subsite has a slug value which contains the current pathname then
 * we re-fetch the new site entry.
 * @param {string} Entry id.
 */
export const fetchContentfulData = async (contentId) => {
  try {
    const allData = await fetchS3Entries();
    const contentful = findEntryById(allData, contentId);

    if (contentful.items.length > 0) {
      const activeSiteId = getActiveSite(contentful);
      if (activeSiteId) {
        return fetchContentfulData(activeSiteId);
      }
    }
    return contentfulParser(contentful);
  } catch (e) {
    console.error('S3 content parse error', e);
    return contentfulParser({ items: [] });
  }
};
